import { assertDemoAllowed, CoreFetch, DEMO_QUICKSIGHT_URL, ReportApi } from '@enter/core';
import { ContactSupport, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { IInjectedRootStore, stream2string } from 'stores';
import { Dashboard } from '../dashboard';
import * as S from './reports.styles';

type IReportsProps = IInjectedRootStore;

export const Reports = inject('rootStore')(
    observer((props: IReportsProps) => {
        const { rootStore } = props;

        const [loading, setLoading] = useState(true);
        const [unavailable, setUnavailable] = useState(false);
        const [error, setError] = useState(false);
        /**
         * Demo mode embeds the DEMO dashboard minted by the demo Lambda (the request below), and
         * falls back to the generated Overview only when that dashboard cannot be shown at all: the
         * Lambda down, the request refused, or AWS refusing the dashboard once the embed loads.
         *
         * An error banner is the worst thing to put in front of a prospect, so the fallback is a
         * modest screen that works rather than a broken embed. It never falls back to the product
         * endpoint, which embeds the signed-in account's real analytics (NS-14).
         *
         * This comment used to describe a same-day exception that showed the REAL account dashboard
         * in demo mode. That exception was withdrawn once the demo Lambda existed — see NS-14,
         * "REPORTS", in `.bearing/northstars.md`.
         */
        const [useGeneratedOverview, setUseGeneratedOverview] = useState(false);
        const isDemo = Boolean(rootStore?.isDemo());

        document.title = 'ENTER Platform - Reports';

        useEffect(() => {
            (async (): Promise<void> => {
                {
                    try {
                        setLoading(true);

                        /**
                         * Demo mode asks the DEMO endpoint; everything else asks the product one.
                         *
                         * John Russell, 2026-09-10: *"When its in demo mode, make this call … When
                         * its out of demo mode, make a real api call."* The two answer on the same
                         * path with the same `{ url }` shape, and differ in what they embed: the
                         * demo Lambda mints an ANONYMOUS embed of a demo dashboard full of
                         * fabricated data, while the product endpoint mints a registered-user
                         * embed of the signed-in account's real analytics. That is why demo mode
                         * can have a genuine QuickSight iframe without breaking NS-14.
                         *
                         * A raw `fetch`, so no middleware sees it — hence the explicit guard, the
                         * same rule the coder-rules dashboard and document transfer follow.
                         * `demo-boundary.coverage.test.ts` fails the build if a raw request
                         * appears without one.
                         */
                        let url: string;
                        if (isDemo) {
                            assertDemoAllowed(DEMO_QUICKSIGHT_URL, 'GET');
                            const response = await fetch(DEMO_QUICKSIGHT_URL, { method: 'GET' });
                            if (!response.ok) {
                                throw new Error(`Demo QuickSight endpoint answered ${response.status}`);
                            }
                            url = (await response.json()).url;
                        } else {
                            url = (await CoreFetch<ReportApi>(ReportApi).getProviderQuicksightUrl()).url;
                        }
                        const QuickSightEmbedding = await import('amazon-quicksight-embedding-sdk');

                        const embeddingContext = await QuickSightEmbedding.createEmbeddingContext();

                        const options = {
                            url,
                            container: document.getElementById('dashboardContainer'),
                            height: '100%',
                            width: '100%',
                            onChange: changeEvent => {
                                switch (changeEvent.eventName) {
                                    case 'CONTENT_LOADED':
                                        setError(false);
                                        setLoading(false);
                                        break;
                                    case 'ERROR_OCCURRED':
                                        // In a demo an error banner is the worst possible screen;
                                        // show the generated Overview instead.
                                        if (isDemo) {
                                            setUseGeneratedOverview(true);
                                        } else {
                                            setError(true);
                                        }
                                        setLoading(false);
                                        break;
                                }
                            },
                        };

                        const contentOptions = {
                            onMessage: async (messageEvent, experienceMetadata) => {
                                console.log('Quicksight Message received: ', messageEvent);
                                switch (messageEvent.eventName) {
                                    case 'CONTENT_LOADED':
                                        setError(false);
                                        setLoading(false);
                                        break;
                                    case 'ERROR_OCCURRED':
                                        if (isDemo) {
                                            setUseGeneratedOverview(true);
                                        } else {
                                            setError(true);
                                        }
                                        setLoading(false);
                                        break;
                                }
                            },
                        };

                        const dashboard = await embeddingContext.embedDashboard(options, contentOptions);
                    } catch (err) {
                        // tslint:disable-next-line: no-console
                        console.error(err);

                        // Whatever went wrong — the demo Lambda down, a refused request, a bad
                        // token exchange — a demo shows the generated Overview rather than an
                        // apology. It is a backstop now, not the expected path: the demo endpoint
                        // needs no per-account provisioning, so it should normally just work.
                        if (isDemo) {
                            setUseGeneratedOverview(true);
                            setLoading(false);
                            return;
                        }

                        if (err && err.body) {
                            const str = await stream2string(err.body);
                            const msg = JSON.parse(str);
                            if (msg.code === 'err.quicksight_configuration_does_not_exist') {
                                setUnavailable(true);
                            } else {
                                setError(true);
                            }
                        } else {
                            setError(true);
                        }
                        setLoading(false);
                    }
                }
            })();
        }, []);

        const renderLoader = (): React.ReactElement => <S.Loader />;
        const renderUnavailable = (): React.ReactElement => (
            <S.Message>
                Sorry! Your account is not configured for reports.
                <ContactSupport action="request access to reports" subject="Requesting access to reports" />
            </S.Message>
        );
        const renderError = (): React.ReactElement => (
            <S.Message>
                Sorry! An error occurred while loading your reports.
                <ContactSupport action="request help or refresh to try again" subject="Need help with reports" />
            </S.Message>
        );

        const renderConditional = (): React.ReactElement | undefined => {
            if (loading) {
                return renderLoader();
            } else if (unavailable) {
                return renderUnavailable();
            } else if (error) {
                return renderError();
            }
        };

        /**
         * The generated Overview, shown when the real dashboard could not be.
         *
         * Every number on it comes from `metricsStore`, which branches on `features.isDemo`, so it
         * is generated by construction (NS-15) and built from design-system components (NS-1).
         *
         * It also replaced a Figma prototype embed whose node (`2802:16320`, page `2446:9041`) no
         * longer exists in "Enter Design Work" — that embed had been rendering whatever frame
         * Figma fell back to, which is what was reported as broken reporting on 2026-09-09.
         */
        if (useGeneratedOverview && rootStore) {
            return <Dashboard rootStore={rootStore} />;
        }

        return (
            <S.Wrapper>
                <PageLayoutRow expand={!unavailable}>
                    {renderConditional()}
                    {!unavailable && <S.Embedded id="dashboardContainer" />}
                </PageLayoutRow>
            </S.Wrapper>
        );
    }),
);
